import React, { useEffect, useState, useCallback } from "react";
import { toast } from "sonner";
import {
  ujmsAudit, ujmsOrg, ujmsOrgCreate, ujmsOrgUpdate, ujmsIntakeConfig,
  ujmsWarrantReturnConfig, ujmsSaveWarrantReturnConfig,
} from "../../services/ujmsApi";
import { Panel, Badge, thCls, tdCls, inputCls, selectCls, btnPrimary, btnGhost, Field, fmtDT, cls, can } from "../ui";

export function AuditTab() {
  const [rows, setRows] = useState([]);
  const [action, setAction] = useState("");
  const load = useCallback(() => {
    ujmsAudit(action ? { action } : {}).then((r) => setRows(r.data)).catch(() => {});
  }, [action]);
  useEffect(() => { load(); }, [load]);
  return (
    <div className="space-y-3">
      <div className="flex gap-2 items-end">
        <Field label="Filter by action"><input className={inputCls} value={action} onChange={(e) => setAction(e.target.value.toUpperCase())} placeholder="e.g. CASE_CREATED" data-testid="audit-action-filter" /></Field>
        <button className={btnGhost} onClick={load} data-testid="audit-refresh">Apply</button>
      </div>
      <Panel title="Append-only audit record — no update or delete path exists" testId="audit-panel">
        <div className="max-h-[520px] overflow-y-auto">
          <table className="w-full border-collapse">
            <thead><tr><th className={thCls}>Timestamp</th><th className={thCls}>Action</th><th className={thCls}>Actor</th><th className={thCls}>Resource</th><th className={thCls}>Reason</th></tr></thead>
            <tbody>
              {rows.map((a) => (
                <tr key={a.id} className="hover:bg-slate-50">
                  <td className={`${tdCls} font-mono text-xs whitespace-nowrap`}>{fmtDT(a.timestamp)}</td>
                  <td className={tdCls}><Badge value={a.action} /></td>
                  <td className={`${tdCls} text-xs`}>{a.user_name}<div className="text-slate-400">{(a.roles || []).join(", ")}</div></td>
                  <td className={`${tdCls} font-mono text-xs`}>{a.resource}</td>
                  <td className={`${tdCls} text-xs`}>{a.reason || "—"}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </Panel>
    </div>
  );
}

export function ConfigTab() {
  const [rules, setRules] = useState([]);
  const [numCfg, setNumCfg] = useState([]);
  const [caseTypes, setCaseTypes] = useState([]);
  const [cfg, setCfg] = useState(null);
  const [nr, setNr] = useState({ division_id: "", case_type: "CRIMINAL", judge_id: "", courtroom_id: "" });
  const [fmt, setFmt] = useState("");
  const [warrantCfg, setWarrantCfg] = useState(null);

  const load = useCallback(() => {
    ujmsOrg("assignment-rules").then((r) => setRules(r.data)).catch(() => {});
    ujmsOrg("case-number-configs").then((r) => { setNumCfg(r.data); setFmt(r.data[0]?.format || ""); }).catch(() => {});
    ujmsOrg("case-types").then((r) => setCaseTypes(r.data)).catch(() => {});
    ujmsIntakeConfig().then((r) => setCfg(r.data)).catch(() => {});
    ujmsWarrantReturnConfig().then((r) => setWarrantCfg(r.data)).catch(() => {});
  }, []);
  useEffect(() => { load(); }, [load]);

  const nameOf = (list, id) => (cfg?.[list] || []).find((x) => x.id === id)?.name || "—";

  const addRule = async () => {
    try {
      await ujmsOrgCreate("assignment-rules", { ...nr, priority: 1, label: `Rule — ${nameOf("judges", nr.judge_id)}` });
      toast.success("Assignment rule created"); load();
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
  };
  const saveFmt = async () => {
    try {
      await ujmsOrgUpdate("case-number-configs", numCfg[0].id, { format: fmt });
      toast.success("Case number format updated"); load();
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
  };
  const saveWarrantCfg = async () => {
    try {
      await ujmsSaveWarrantReturnConfig(warrantCfg.windows);
      toast.success("Warrant return windows updated"); load();
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
  };

  return (
    <div className="grid grid-cols-1 xl:grid-cols-2 gap-4">
      <Panel title="Case assignment rules (round-robin per division/type)" testId="rules-panel">
        <table className="w-full border-collapse">
          <thead><tr><th className={thCls}>Division</th><th className={thCls}>Case Type</th><th className={thCls}>Judge</th><th className={thCls}>Courtroom</th></tr></thead>
          <tbody>
            {rules.map((r) => (
              <tr key={r.id}><td className={tdCls}>{nameOf("divisions", r.division_id)}</td><td className={tdCls}><Badge value={r.case_type} /></td>
                <td className={tdCls}>{nameOf("judges", r.judge_id)}</td><td className={tdCls}>{nameOf("courtrooms", r.courtroom_id)}</td></tr>
            ))}
          </tbody>
        </table>
        {cfg && (
          <div className="p-3 flex flex-wrap items-end gap-2 border-t border-slate-200">
            <Field label="Division"><select className={`${selectCls} w-40`} value={nr.division_id} onChange={(e) => setNr({ ...nr, division_id: e.target.value })} data-testid="rule-division"><option value="">Select…</option>{cfg.divisions.map((d) => <option key={d.id} value={d.id}>{d.name}</option>)}</select></Field>
            <Field label="Case Type"><select className={`${selectCls} w-32`} value={nr.case_type} onChange={(e) => setNr({ ...nr, case_type: e.target.value })} data-testid="rule-type">{caseTypes.map((t) => <option key={t.code} value={t.code}>{t.code}</option>)}<option value="*">* (any)</option></select></Field>
            <Field label="Judge"><select className={`${selectCls} w-44`} value={nr.judge_id} onChange={(e) => setNr({ ...nr, judge_id: e.target.value })} data-testid="rule-judge"><option value="">Select…</option>{cfg.judges.map((j) => <option key={j.id} value={j.id}>{j.name}</option>)}</select></Field>
            <Field label="Courtroom"><select className={`${selectCls} w-36`} value={nr.courtroom_id} onChange={(e) => setNr({ ...nr, courtroom_id: e.target.value })} data-testid="rule-room"><option value="">—</option>{cfg.courtrooms.map((r) => <option key={r.id} value={r.id}>{r.name}</option>)}</select></Field>
            <button className={btnPrimary} disabled={!nr.division_id || !nr.judge_id} onClick={addRule} data-testid="rule-add-btn">Add Rule</button>
          </div>
        )}
      </Panel>

      <div className="space-y-4">
        <Panel title="Case number service configuration" testId="numbering-panel">
          <div className="p-3 space-y-2">
            {numCfg.map((c) => (
              <div key={c.id} className="text-xs font-mono text-slate-500">scope: {c.case_type} · seq_start: {c.seq_start}</div>
            ))}
            <div className="flex gap-2 items-end">
              <Field label="Format pattern"><input className={`${inputCls} font-mono`} value={fmt} onChange={(e) => setFmt(e.target.value)} data-testid="numbering-format-input" /></Field>
              <button className={btnGhost} onClick={saveFmt} data-testid="numbering-save-btn">Save</button>
            </div>
            <p className="text-xs text-slate-400">Tokens: {"{year}"}, {"{yy:02d}"}, {"{type_code}"}, {"{seq:06d}"}. Example result: 26CR700001. Numbers are atomic and never reused.</p>
          </div>
        </Panel>
        <Panel title="Warrant return windows (auto-scheduled return hearings)" testId="warrant-windows-panel">
          <div className="p-3 space-y-2">
            {["BENCH", "ARREST", "SEARCH"].map((t) => (
              <div key={t} className="flex items-center gap-3">
                <span className="w-24 text-sm font-semibold">{t}</span>
                <select className={`${selectCls} w-56`} value={warrantCfg?.windows?.[t] ?? 1}
                  onChange={(e) => setWarrantCfg({ ...warrantCfg, windows: { ...(warrantCfg?.windows || {}), [t]: Number(e.target.value) } })}
                  data-testid={`warrant-window-${t.toLowerCase()}`}>
                  <option value={1}>Next business day</option>
                  <option value={3}>3 business days</option>
                  <option value={5}>5 business days</option>
                </select>
              </div>
            ))}
            <button className={btnPrimary} disabled={!warrantCfg} onClick={saveWarrantCfg} data-testid="warrant-windows-save">Save Windows</button>
            <p className="text-xs text-slate-400">When a warrant is issued, its return hearing is auto-set this many business days out — weekends and full court closures are skipped.</p>
          </div>
        </Panel>
        <Panel title="Case type catalog" testId="casetypes-panel">
          <table className="w-full border-collapse">
            <thead><tr><th className={thCls}>Code</th><th className={thCls}>Label</th><th className={thCls}>Number Code</th><th className={thCls}>Enabled</th></tr></thead>
            <tbody>
              {caseTypes.map((t) => (
                <tr key={t.id}><td className={`${tdCls} font-mono text-xs`}>{t.code}</td><td className={tdCls}>{t.label}</td>
                  <td className={`${tdCls} font-mono text-xs`}>{t.number_code}</td><td className={tdCls}>{t.enabled ? "Yes" : "No"}</td></tr>
              ))}
            </tbody>
          </table>
        </Panel>
      </div>
    </div>
  );
}
